/* eslint-disable max-len */
import React, { Component } from 'react';
import { Row, TabContainer, Nav, NavItem, TabContent, TabPane } from 'react-bootstrap';
import get from 'lodash/get';
import { connect } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toastr } from 'react-redux-toastr';
import { AdvertiserApi } from '../../../../api';
import { ContentHolder } from '../../../../components/common';
import { ButtonCopy } from '../../../../components/common/buttons';

class Show extends Component {
  componentDidMount() {
    this.fetchResource();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.account.uuid !== this.props.account.uuid) {
      this.fetchResource();
    }
  }

  fetchResource(params = {}) {
    const { account, dispatch } = this.props;
    const pixel = get(this.props, 'pixel.data', {});
    if (account.uuid && pixel.advertiser_account_id !== account.uuid) {
      const data = { ...params, account_id: account.uuid };
      dispatch(AdvertiserApi.actions.pixel.get(data));
    }
  }

  render() {
    const { pixel, isLoading } = this.props;
    const pixelId = get(pixel, 'data.id', null);
    const headSnippet = `
    <script async src="//script.jobadx.com/pixel-a.js" type="text/javascript"></script>
<script>
  window.ajdxLayer = window.ajdxLayer || [] function ajdxtag(){ajdxLayer.push(arguments)}
  ajdxtag("pixel", "${pixelId}")
</script>`.trim();
    const iframeHeadSnippet = `ajdxtag("iframe", "<website url in iframe>") // Replace <website url in iframe> with the url of the website rendering the job`;
    const approachOneSnippet = `<script>ajdxtag("conv")</script>`;
    const approachOneIframeSnippet = `
    <script>
  function ajdxitag(a,b,c=null){window.parent.postMessage(["iframe",b,c],a)}
  ajdxitag("https://www.your-website.com", "conv")
</script>
    `.trim();
    const approachTwoSnippet = `
      <script>
  ajdxtag("jobId", "<xyz>") // Replace <xyz>, with the job id dynamically
</script>
`.trim();
    const approachTwoIframeSnippet = `
    <script>
  function ajdxitag(a,b,c=null){window.parent.postMessage(["iframe",b,c],a)}
  ajdxitag("https://www.your-website.com", "jobId", "<xyz>") // Replace <xyz>, with the job id dynamically
</script>
    `.trim();
    const approachThreeSnippet = `
      <script>
  ajdxtag('clickId', "<xyz>") // Dynamically replace <xyz> with the click identifier we pass to you in the aclk URL parameter
</script>
 `.trim();
    const approachThreeIframeSnippet = `
    <script>
  function ajdxitag(a,b,c=null){window.parent.postMessage(["iframe",b,c],a)}
  ajdxitag("https://www.your-website.com", "clickId", "<xyz>")
  // Dynamically replace <xyz> with the click identifier we pass to you in the aclk URL parameter
</script>
    `.trim();

    return (
      <div className="ibox">
        <div className="ibox-title">
          <h5>Pixel</h5>
        </div>
        <div className="ibox-content">
          <ContentHolder loading={isLoading}>
            <div className="pixel-documentation-container">
              <p>
                In order to have conversions recorded in our system we need you to let us know every time you receive a conversion. This can be done
                in one of two ways:
              </p>
              <ul>
                <li>Pixel: where installing our pixel on your website lets you trigger conversion request through it</li>
                <li>Server to Server: you send a conversion request manually to our server conversion endpoint</li>
              </ul>

              <p>Choose your integration preference using the tabs below:</p>

              <div className="pixel-tab-container">
                <TabContainer id="template-tabs" defaultActiveKey="pixel">
                  <div>
                    <Row>
                      <Nav bsStyle="tabs">
                        <NavItem eventKey="pixel">
                          <span className="">Pixel</span>
                        </NavItem>
                        <NavItem eventKey="server">
                          <span className="">Server to Server</span>
                        </NavItem>
                      </Nav>
                    </Row>
                    <Row>
                      <TabContent>
                        <TabPane eventKey="pixel" className="custom-tab-pane">
                          <h2>STEP 1</h2>
                          <p>
                            Paste the javascript tag at the bottom of the body section, just above the closing <code>&lt;/body&gt;</code> tag or in
                            the <code>&lt;head&gt;</code>.{' '}
                            <strong>
                              Please make sure the following javascript tag is available across all pages or at least on all pages the job seeker
                              lands on up to and including the confirmation apply page:
                            </strong>
                          </p>

                          <pre className="full-block">
                            <code className="language-html">{headSnippet}</code>
                          </pre>

                          <CopyToClipboard text={headSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <strong>
                            <p>
                              NOTE: If jobs on your website are rendered in an iframe then you need to add an additional line to the script above
                              after <code>ajdxtag(&quot;pixel&quot;, &quot;{pixelId}&quot;)</code>. This script must be in the main website and not in
                              the iframe.
                            </p>
                          </strong>

                          <pre className="full-block">
                            <code className="language-html">{iframeHeadSnippet}</code>
                          </pre>

                          <CopyToClipboard text={iframeHeadSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <h2>STEP 2</h2>

                          <p>
                            Whenever a jobseeker successfully completes the application, a conversion event needs to be triggered. In order to do that
                            you need to place the snippet at the application completion or thank you page. There are 3 different ways in which you can
                            trigger a conversion event. Approach 2 and 3 would require more technical implementation.
                          </p>

                          <strong>
                            <p>NOTE: Add the event code just below or after the main javascript tag from STEP 1.</p>
                          </strong>

                          <h3>APPROACH 1</h3>
                          <p>
                            This is the simplest way to trigger a conversion, but there are some scenarios where it may not trigger the conversion
                            properly. So the accuracy of conversions recorded is lowest among the rest.
                          </p>

                          <pre className="full-block">
                            <code className="language-html">{approachOneSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachOneSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <strong>
                            <p>
                              NOTE: If you are triggering the conversion within an iframe then you need to use the code below instead (replace the URL
                              with your website URL)
                            </p>
                          </strong>

                          <pre className="full-block">
                            <code className="language-html">{approachOneIframeSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachOneIframeSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <h3>APPROACH 2</h3>
                          <p>
                            This approach requires you to dynamically pass the <strong>job id</strong> in the conversion tag. This is a much better
                            approach than approach 1 but requires little more integration from you. It takes care of most of the scenarios leading to
                            higher accuracy than the approach 1. Please make sure that the job id is the same as the one that you sent us in the xml
                            feed.
                          </p>

                          <pre className="full-block">
                            <code className="language-html">{approachTwoSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachTwoSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <strong>
                            <p>
                              NOTE: If you are triggering the conversion within an iframe then you need to use the code below instead (replace the URL
                              with your website URL)
                            </p>
                          </strong>

                          <pre className="full-block">
                            <code className="language-html">{approachTwoIframeSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachTwoIframeSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <h3>APPROACH 3</h3>
                          <p>This approach has the highest accuracy but requires more integration than approach 2.</p>

                          <div className="pixel-info-box alert alert-warning">
                            <p>PRE-STEP - Click url setup</p>

                            <p>
                              When a jobseeker clicks a job link within the JobAdX network, JobAdX server will add click_id to the destination URL
                              before redirection happens.
                            </p>
                            <p>https://example.com/job?aclk=&#123;click_id&#125;</p>
                            <p>When the jobseeker reaches the landing page the advertiser must store the value of aclk (click_id).</p>
                            <p>aclk=&#123;click_id&#125;</p>
                          </div>

                          <p>Place the snippet below at the application completion or thank you page.</p>

                          <pre className="full-block">
                            <code className="language-html">{approachThreeSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachThreeSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>

                          <strong>
                            <p>
                              NOTE: If you are triggering the conversion within an iframe then you need to use the code below instead (replace the URL
                              with your website URL)
                            </p>
                          </strong>

                          <pre className="full-block">
                            <code className="language-html">{approachThreeIframeSnippet}</code>
                          </pre>

                          <CopyToClipboard text={approachThreeIframeSnippet} onCopy={() => toastr.success('Event code copied to clipboard!')}>
                            <ButtonCopy btnLabel="Copy to clipboard" />
                          </CopyToClipboard>
                        </TabPane>
                        <TabPane eventKey="server" className="custom-tab-pane">
                          <h2>STEP 1</h2>
                          <p>
                            First you must setup the ability to store the click identifier. When a visitor clicks an ad link within the JobAdX
                            network, the JobAdX server will add a click_id to the destination URL before redirection happens under the parameter{' '}
                            <code>aclk</code>.
                          </p>

                          <pre className="full-block">
                            <code>https://example.com/job?aclk=&#123;click_id&#125;</code>
                          </pre>

                          <p>When the visitor reaches the landing page you must store the key/value pair.</p>

                          <pre className="full-block">
                            <code>aclk=&#123;click_id&#125;</code>
                          </pre>

                          <h2>STEP 2</h2>

                          <p>
                            Next you will need to setup the postback functionality. When the visitor converts, your server must send the stored
                            key/value pair to the JobAdX server via a post request. The click_id is used for identifying and associating a click to
                            the new conversion in the JobAdX network.
                          </p>

                          <pre className="full-block">
                            <code>https://job.jobadx.com/sconv?aclk=&#123;click_id&#125;</code>
                          </pre>
                          <p>
                            An authorization token which is unique to your advertiser account, must be set in the header to identify and secure
                            requests. The one for this account is included in the following authorization header:
                          </p>

                          <pre className="full-block">
                            <code>‘Authorization: token={pixelId}’</code>
                          </pre>

                          <h2>Example Request</h2>

                          <pre className="full-block">
                            <code>curl –request POST -H ‘Authorization: token={pixelId}’ https://job.jobadx.com/sconv?aclk=&#123;click_id&#125;</code>
                          </pre>
                          <h3>Response</h3>

                          <ul>
                            <li>200 Ok - Conversion recorded.</li>
                            <li>401 Unauthorized - Invalid/no token found.</li>
                          </ul>

                          <p>
                            Conversion for a click_id in the url will be recorded only once, If the same click_id is sent more than once, it will
                            override the corresponding conversion.
                          </p>
                        </TabPane>
                      </TabContent>
                    </Row>
                  </div>
                </TabContainer>
              </div>
            </div>
          </ContentHolder>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    advertiser,
    advertiser: { accounts },
  } = state;
  const {
    params: { accountSlug },
  } = ownProps;
  const account = (accounts.data.data || []).filter(w => w.slug === accountSlug)[0] || {};

  return { pixel: advertiser.pixel.data, isLoading: advertiser.pixel.loading, account };
};

export default connect(mapStateToProps)(Show);
